/* @HEADER
 * @version 1.0.1674 | 2026-09-20
 * @purpose The two decisions of ZVX-DEV-6227, pure and clock-free: whether a dismissed update prompt returns for a NEWER build, and whether a tab that was hidden long enough applies a known update on return without asking — plus the hidden-time tracker that feeds the second.
 * @sidecar idleApply.ts.header.md
 * @layer app | @hex _none_ | @ctx delivery
 * @public true
 * @edit careful
 */

// SpecRefs: ZVX-DEV-6227

import { isNewerVersionAvailable } from './versionHeartbeat.ts';

/**
 * ## Why these are pure functions and not lines inside registerSW.ts
 *
 * Both decisions used to be implicit and both were wrong in the same way:
 * `dismissed` was terminal for the session and `heartbeatFired` never reset
 * while versions differed, so a person who closed "update to A" never heard
 * of B. Written as functions over plain values they can be proven with a
 * fake clock; ZVX-DEV-3717 records what happened to the last four fixes of
 * this prompt that were proven only through DOM mocks.
 */

/** Hidden at least this long, and the tab is treated as unattended (operator's word, 2026-09-20: 30 minutes). */
export const IDLE_APPLY_THRESHOLD_MS = 30 * 60 * 1000;

/**
 * A prompt dismissed for `dismissedVersion` returns only for a build strictly
 * newer than that; the same build or an older one leaves the dismissal in
 * effect. No dismissal on record → any offer shows.
 */
export function shouldReoffer({
  dismissedVersion,
  offeredVersion,
}: {
  dismissedVersion: string | null | undefined;
  offeredVersion: string | null | undefined;
}): boolean {
  if (!dismissedVersion) return true;
  return isNewerVersionAvailable(dismissedVersion, offeredVersion);
}

/**
 * The heartbeat side of the same rule: dispatch an offer when nothing was
 * offered yet, or when the server's build is strictly newer than the last
 * one offered. The old guard — "already fired, stay quiet" — is kept for the
 * SAME version, which is what it was for (no noisy re-dispatch every poll).
 */
export function shouldDispatchOffer({
  lastOfferedVersion,
  serverVersion,
}: {
  lastOfferedVersion: string | null;
  serverVersion: string | null | undefined;
}): boolean {
  if (lastOfferedVersion === null) return true;
  return isNewerVersionAvailable(lastOfferedVersion, serverVersion);
}

export type IdleApplyDecision =
  | { apply: true; hiddenForMs: number }
  | { apply: false; reason: 'not-hidden-long-enough' | 'no-newer-build' | 'active-edit' };

/**
 * The quiet path. All three guards, in order, each one enough to refuse:
 * the tab was hidden for at least the threshold (nobody was at it), a
 * newer build is already known (the heartbeat found it while hidden or
 * before), and nothing is being edited at the moment of return. Distinct
 * from the ZVX-DEV-3680 regression on purpose: that one reloaded while a
 * person was typing; this one never fires unless the tab was unattended
 * for the whole threshold.
 */
export function decideIdleApply({
  hiddenForMs,
  newerBuildKnown,
  activeEdit,
  thresholdMs = IDLE_APPLY_THRESHOLD_MS,
}: {
  hiddenForMs: number | null;
  newerBuildKnown: boolean;
  activeEdit: boolean;
  thresholdMs?: number;
}): IdleApplyDecision {
  if (hiddenForMs === null || hiddenForMs < thresholdMs) {
    return { apply: false, reason: 'not-hidden-long-enough' };
  }
  if (!newerBuildKnown) return { apply: false, reason: 'no-newer-build' };
  if (activeEdit) return { apply: false, reason: 'active-edit' };
  return { apply: true, hiddenForMs };
}

/**
 * The "someone is in the middle of something" signal this slice actually
 * uses, stated so it can be checked rather than assumed: a focused editable
 * element (input, textarea, contenteditable) or an open dialog. Everything
 * else the app persists locally with a debounced autosave that main.tsx
 * flushes on `visibilitychange → hidden`, so by the time the threshold has
 * elapsed the stores are on disk; the flush is repeated before the reload
 * as belt and braces (see UpdateAvailableNotice's auto-apply path).
 */
export function hasActiveEdit(doc: Document): boolean {
  const el = doc.activeElement;
  if (el && el !== doc.body) {
    const tag = el.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
    if ((el as HTMLElement).isContentEditable) return true;
  }
  return (
    doc.querySelector('[role="dialog"][open], dialog[open], [role="dialog"]:not([hidden])') !== null
  );
}

/**
 * How long the document has been hidden, fed by visibilitychange. Returns
 * null while visible or before the first hide. `now` is injected so the
 * decision can be driven with a fake clock.
 */
export function createHiddenTracker(now: () => number = () => Date.now()) {
  let hiddenSince: number | null = null;
  return {
    onHidden() {
      if (hiddenSince === null) hiddenSince = now();
    },
    /** Called on return: reports the hidden span and resets. */
    onVisible(): number | null {
      if (hiddenSince === null) return null;
      const span = now() - hiddenSince;
      hiddenSince = null;
      return span;
    },
  };
}
